import type { Data } from '@strapi/types';
import type { FunctionComponent } from 'react';

import { useCheckTranslatedString } from '@/modules/utils/custom-hooks/use-check-translated-string';
import { Button } from '@/ui-components/button/button';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { Input } from '@/ui-components/input/input';
import { LoadingIndicator } from '@/ui-components/loading-indicator/loading-indicator';
import { Paragraph } from '@/ui-components/paragraph/paragraph';

import { useStatusParams } from '../hooks/use-status-params';
import { ColorPicker } from './color-picker';
import type { DocumentStatusListProps } from './types';

export const DocumentStatusList: FunctionComponent<DocumentStatusListProps> = ({
  documentStatuses,
  statusState,
  handleStatusState,
  clearStatusState
}) => {
  const {
    t,
    statusData,
    error,
    isPending,
    handleSaveButton,
    handleEditButton,
    handleStatusChange,
    handleDeleteButton,
    handleCancelButton
  } = useStatusParams({ clearStatusState, handleStatusState });
  const checkTranslatedString = useCheckTranslatedString();

  const renderActionButtons = (status: Data.Entity<'api::project-status.project-status'>) => {
    if (isPending) return <LoadingIndicator sizing='small' color='primary' />;
    const { documentId } = status;

    if (statusState.currentId !== documentId && !isPending) {
      return (
        <Button
          variant='text'
          className='font-normal text-sm w-fit text-dark dark:text-n700_text_dark_mode'
          disabled={
            statusState.isNew ||
            (!statusData[documentId]?.name && statusState.currentId === documentId) ||
            (statusState.currentId !== status.documentId && !!statusState.currentId)
          }
          onClick={() => handleEditButton(status)}
        >
          {t('T_EDIT')}
        </Button>
      );
    }

    return (
      <Button
        variant='text'
        className={`font-normal text-sm w-fit ${statusData[documentId]?.name && statusState.currentId === documentId ? 'text-accent' : 'text-dark'}`}
        disabled={statusState.isNew || (!statusData[documentId]?.name && statusState.currentId === documentId)}
        onClick={() => handleSaveButton(status)}
      >
        {t('T_SAVE')}
      </Button>
    );
  };

  const renderDeleteButton = (status: Data.Entity<'api::project-status.project-status'>) => {
    if (status.isDefault) {
      if (statusState.currentId !== status.documentId) {
        return <Paragraph variant='text-sm' className='w-fit'>{`*${t('T_DEFAULT')}`}</Paragraph>;
      }

      return (
        <Button
          variant='text'
          className={'text-dark font-normal text-sm w-fit dark:text-n700_text_dark_mode hover:text-brand'}
          onClick={() => handleCancelButton(status)}
        >
          {t('T_CANCEL')}
        </Button>
      );
    }

    return (
      <Button
        variant='text'
        disabled={statusState.isNew || (statusState.currentId !== status.documentId && !!statusState.currentId)}
        className={`text-dark font-normal text-sm w-fit dark:text-n700_text_dark_mode hover:text-brand ${isPending ? 'cursor-not-allowed opacity-50' : 'cursor-pointer opacity-100'}`}
        onClick={
          statusState.currentId !== status.documentId ? () => handleDeleteButton(status.documentId) : () => handleCancelButton(status)
        }
      >
        {statusState.currentId !== status.documentId ? t('T_DELETE') : t('T_CANCEL')}
      </Button>
    );
  };

  return (
    <Flexbox direction='col' className='max-h-[250px] overflow-x-hidden overflow-y-scroll p-0.5 scrollbar-rounded'>
      {documentStatuses
        ?.sort((a) => (a.isDefault ? -1 : 1))
        .map((status) => (
          <Flexbox key={status.documentId} justify={'start'} className='gap-3 mb-2.5 items-baseline'>
            <Input
              value={statusData[status.documentId]?.name ? t(statusData[status.documentId]?.name) : checkTranslatedString(status.name)}
              name='statusName'
              sizing='small'
              disabled={statusState.currentId !== status.documentId}
              onChange={(e) => handleStatusChange({ documentId: status.documentId, newName: e.target.value })}
              placeholder={t('T_ENTER_STATUS_NAME')}
              containerClassname='w-[220px]'
              error={statusState.currentId === status.documentId ? error.message : ''}
            />
            <ColorPicker
              label={statusData[status.documentId]?.name ? t(statusData[status.documentId]?.name) : checkTranslatedString(status.name)}
              color={statusData[status.documentId]?.color ?? status.color}
              isDisabled={statusState.currentId !== status.documentId}
              onColorChange={(color) => handleStatusChange({ documentId: status.documentId, newColor: color })}
              key={status.documentId}
            />

            <Flexbox className='gap-1 grow' justify='start' items='center'>
              {renderActionButtons(status)}
              {renderDeleteButton(status)}
            </Flexbox>
          </Flexbox>
        ))}
    </Flexbox>
  );
};
